import { ApolloClient, split, from } from "@apollo/client";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { createClient } from "graphql-ws";
import { createAuthLink, createErrorLink, createHttpLink, retryLink } from "./links.js";
import { createCache } from "./cache.js";
const toWsUrl = (url) => url.replace("http://", "ws://").replace("https://", "wss://");
const deriveDefaultWsUrlFromBaseUrl = (baseUrl) => {
    try {
        const parsed = new URL(baseUrl);
        return `${parsed.protocol}//${parsed.host}/`;
    }
    catch {
        return baseUrl;
    }
};
const createWebSocketLink = (wsUrl, getToken, webSocketImpl) => {
    const wsUrlFormatted = toWsUrl(wsUrl);
    const wsClient = createClient({
        url: wsUrlFormatted,
        connectionParams: async () => {
            const token = await getToken();
            return token ? { authorization: `Bearer ${token}` } : {};
        },
        ...(webSocketImpl ? { webSocketImpl: webSocketImpl } : {}),
        shouldRetry: () => true,
        retryAttempts: 5,
        retryWait: async (retries) => {
            await new Promise((resolve) => {
                setTimeout(resolve, 1000 * Math.pow(2, retries));
            });
        },
    });
    return new GraphQLWsLink(wsClient);
};
const createSplitLink = (httpLink, wsLink) => {
    return split(({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === "OperationDefinition" && definition.operation === "subscription";
    }, wsLink, httpLink);
};
export const createApolloClient = (options) => {
    const { baseUrl, wsUrl, cache, getToken, observability, webSocketImpl } = options;
    const httpLink = createHttpLink(baseUrl);
    const resolvedWsUrl = wsUrl ?? deriveDefaultWsUrlFromBaseUrl(baseUrl);
    const wsLink = createWebSocketLink(resolvedWsUrl, getToken, webSocketImpl);
    const authLink = createAuthLink(getToken);
    const errorLink = createErrorLink(observability);
    const splitLink = createSplitLink(httpLink, wsLink);
    const link = from([retryLink, errorLink, authLink, splitLink]);
    return new ApolloClient({
        link,
        cache: cache ?? createCache(),
        defaultOptions: {
            watchQuery: {
                fetchPolicy: "cache-and-network",
                errorPolicy: "all",
            },
            query: {
                fetchPolicy: "cache-first",
                errorPolicy: "all",
            },
            mutate: {
                errorPolicy: "all",
            },
        },
    });
};
